import * as React from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { useState, useEffect, useCallback, useMemo } from "react";
import { appConstants } from "../_constants/app.constants";
import { isLikelyTournamentGuid, resolveTournamentIdFromPublicRoutes } from "../_utils/tournamentRouteUtils";
import { useAuth } from "../auth/AuthContext";
import { useConfirm } from "../common/ConfirmDialog";
import { useTournamentPageView } from "../analytics/useTournamentPageView";
import { buildTournamentSeo, usePageMetadata } from "../seo/usePageMetadata";
import {
  Button,
  LoadingState,
  Toast,
  Badge,
  IconArrowLeft,
  IconSoccer,
  IconSettings,
  IconStar,
  IconTrophy,
  IconTv,
  IconUserPlus,
} from "../ui";

const cardClass =
  "rounded-2xl border border-arena/12 bg-white/95 p-5 shadow-sm backdrop-blur-sm sm:p-6";
const sectionTitleClass = "font-display text-xs font-bold uppercase tracking-wider text-ink-muted";
const linkRowClass =
  "focus-ring flex items-center gap-3 rounded-xl border border-arena/10 bg-arena/[0.03] px-4 py-3 text-sm font-semibold text-ink transition hover:border-arena/25 hover:bg-arena/5";

function roleLabel(role) {
  if (!role) return null;
  const map = {
    Owner: "Organizador",
    Admin: "Administrador",
    LiveOperator: "Operador de mesa",
    Viewer: "Pantalla / visor",
  };
  return map[role] || role;
}

function visibilityLabel(v) {
  if (v === 2) return { text: "Público", variant: "success" };
  if (v === 1) return { text: "Solo con enlace", variant: "info" };
  return { text: "Privado", variant: "neutral" };
}

function defaultProgress() {
  return {
    teamCount: 0,
    groupCount: 0,
    allGroupsHaveTeams: false,
    groupMatchCount: 0,
    bracketMatchCount: 0,
  };
}

function tStr(obj, camelKey, pascalKey) {
  if (!obj) return "";
  const v = obj[camelKey] ?? obj[pascalKey];
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
}

function HubMark({ src }) {
  if (src) {
    return <img src={src} alt="" className="max-h-full max-w-full object-contain p-2" />;
  }
  return (
    <span className="flex h-full w-full items-center justify-center">
      <IconTrophy size={40} className="text-arena/45" aria-hidden />
    </span>
  );
}

function HubFlank({ src, className }) {
  if (src) {
    return <img src={src} alt="" className={className} />;
  }
  return (
    <span
      className={`inline-flex items-center justify-center rounded-lg bg-arena/[0.06] text-arena ring-2 ring-arena/10 ${className}`}
      aria-hidden
    >
      <IconTrophy size={28} className="opacity-65" />
    </span>
  );
}

export default function TournamentActions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [tournament, setTournament] = useState(null);
  const [access, setAccess] = useState(null);
  const [canonicalId, setCanonicalId] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [copyMsg, setCopyMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasGames, setHasGames] = useState(false);
  const { tournamentId: routeKey } = useParams();

  const API = appConstants.API_URL;
  useTournamentPageView(canonicalId, "overview");

  const seo = useMemo(() => buildTournamentSeo(tournament), [tournament]);
  usePageMetadata(seo || {
    title: "Torneo | Club Evolver",
    description: "Consulta grupos, partidos, resultados y eliminatorias del torneo.",
    canonicalPath: routeKey ? `/tournamentactions/${encodeURIComponent(routeKey)}` : "/",
    robots: "index,follow",
  });

  const refreshAccess = useCallback(async (tid) => {
    if (!tid) {
      setAccess(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/tournaments/${tid}/access`);
      setAccess(res.data);
    } catch {
      setAccess(null);
    }
  }, [API]);

  useEffect(() => {
    let cancelled = false;

    const fetchTournament = async () => {
      try {
        setLoading(true);
        setLoadError("");
        setCanonicalId(null);
        const trimmed = routeKey ? String(routeKey).trim() : "";
        if (!trimmed) {
          setTournament(null);
          setAccess(null);
          setLoadError("Torneo no especificado.");
          return;
        }

        let effectiveId = trimmed;
        if (!isLikelyTournamentGuid(trimmed)) {
          const resolved = await resolveTournamentIdFromPublicRoutes(API, trimmed);
          if (cancelled) return;
          if (!resolved) {
            setTournament(null);
            setAccess(null);
            setLoadError("No se encontró el torneo (revisa el enlace o el código compartido).");
            return;
          }
          effectiveId = resolved;
        }

        if (cancelled) return;
        setCanonicalId(effectiveId);

        let tournamentData = null;
        try {
          const res = await axios.get(`${API}/api/tournaments/${effectiveId}`);
          tournamentData = res.data;
          if (!cancelled) await refreshAccess(effectiveId);
        } catch (err) {
          const status = err.response?.status;
          if (status === 401 || status === 403) {
            const pub = await axios.get(`${API}/api/public/tournaments/by-id/${effectiveId}`);
            tournamentData = pub.data;
            if (!cancelled) setAccess(null);
          } else {
            throw err;
          }
        }
        if (!cancelled) setTournament(tournamentData);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadError("No se pudo cargar el torneo.");
          setTournament(null);
          setAccess(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTournament();
    return () => {
      cancelled = true;
    };
  }, [routeKey, API, refreshAccess]);

  // Lightweight check so we only surface the "Juegos" entry points when the
  // organizer has actually created games. The endpoint is AllowAnonymous +
  // RequireView, so it works for audience (public) and staff (private) alike.
  useEffect(() => {
    let cancelled = false;
    if (!canonicalId) {
      setHasGames(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/api/tournamentgames/${canonicalId}/games`);
        if (!cancelled) setHasGames(Array.isArray(res.data) && res.data.length > 0);
      } catch {
        if (!cancelled) setHasGames(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canonicalId, API]);

  const toggleFavorite = async () => {
    if (!access || !canonicalId) return;
    setActionBusy(true);
    setActionMsg("");
    try {
      if (access.isFavorite) {
        await axios.delete(`${API}/api/tournaments/${canonicalId}/favorite`);
      } else {
        await axios.post(`${API}/api/tournaments/${canonicalId}/favorite`);
      }
      await refreshAccess(canonicalId);
    } catch (e) {
      setActionMsg(e.response?.data?.message || "No se pudo actualizar favoritos.");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteTournament = async () => {
    if (!canonicalId) return;
    setActionBusy(true);
    setActionMsg("");
    try {
      const { data } = await axios.delete(`${API}/api/tournaments/${canonicalId}`);
      if (data?.requiresConfirmation) {
        setActionBusy(false);
        if (!(await confirm({
          title: "Eliminar torneo",
          message: data.warningMessage || "¿Estás seguro de que quieres eliminar este torneo? Esta acción no se puede deshacer.",
          confirmText: "Eliminar",
          tone: "danger",
        }))) return;
        setActionBusy(true);
        await axios.delete(`${API}/api/tournaments/${canonicalId}?confirmed=true`);
      }
      navigate("/mis-torneos");
    } catch (e) {
      setActionMsg(e.response?.data?.message || "No se pudo eliminar el torneo.");
    } finally {
      setActionBusy(false);
    }
  };

  const title = (tournament && (tournament.name || tournament.Name)) || "Torneo";
  const rawDesc = tournament && (tournament.description ?? tournament.Description);
  const description =
    rawDesc && String(rawDesc).trim() !== String(title).trim() ? String(rawDesc).trim() : "";

  const isFinished = Boolean(tournament?.isFinished ?? tournament?.IsFinished);
  const visibilityRaw = tournament?.visibility ?? tournament?.Visibility;
  let visibilityNum = typeof visibilityRaw === "number" && !Number.isNaN(visibilityRaw) ? visibilityRaw : 0;
  // Respuesta pública (by-id) no incluye visibilidad; esos torneos son listados como públicos.
  if (typeof visibilityRaw !== "number" && !access && tournament) {
    visibilityNum = 2;
  }
  const visInfo = visibilityLabel(visibilityNum);

  const progress = access?.setupProgress ?? defaultProgress();
  const p = {
    teamCount: Number(progress.teamCount ?? 0),
    groupCount: Number(progress.groupCount ?? 0),
    allGroupsHaveTeams: Boolean(progress.allGroupsHaveTeams),
    groupMatchCount: Number(progress.groupMatchCount ?? 0),
    bracketMatchCount: Number(progress.bracketMatchCount ?? 0),
  };

  const wizardSteps = useMemo(() => {
    const setupTo = canonicalId ? `/tournamentactions/${canonicalId}/setup` : "#";
    const s1 = p.teamCount >= 2;
    const s2 = p.groupCount >= 1 && p.allGroupsHaveTeams;
    const s3 = p.groupMatchCount > 0;
    const s4 = p.bracketMatchCount > 0 || isFinished;
    return [
      {
        key: "teams",
        label: "Equipos",
        done: s1,
        hint: "Al menos dos equipos en el torneo.",
        to: setupTo,
        hash: "#equipos",
      },
      {
        key: "groups",
        label: "Grupos",
        done: s2,
        hint: "Crea grupos y asigna equipos.",
        to: setupTo,
        hash: "#grupos",
      },
      {
        key: "schedule",
        label: "Calendario",
        done: s3,
        hint: "Genera partidos de grupos.",
        to: setupTo,
        hash: "#calendario",
      },
      {
        key: "bracket",
        label: "Eliminatoria",
        done: s4,
        hint: "Opcional: configura fase final cuando toque.",
        to: setupTo,
        hash: "#eliminatoria",
      },
    ];
  }, [p.teamCount, p.groupCount, p.allGroupsHaveTeams, p.groupMatchCount, p.bracketMatchCount, isFinished, canonicalId]);

  const nextStep = useMemo(() => wizardSteps.find((s) => !s.done) ?? null, [wizardSteps]);
  const completedCount = wizardSteps.filter((s) => s.done).length;

  const viewerUrl =
    typeof window !== "undefined" && canonicalId ? `${window.location.origin}/viewer/${canonicalId}` : "";

  const copyViewerLink = async () => {
    if (!viewerUrl) return;
    try {
      await navigator.clipboard.writeText(viewerUrl);
      setCopyMsg("Enlace copiado");
      window.setTimeout(() => setCopyMsg(""), 2500);
    } catch {
      setCopyMsg("No se pudo copiar");
      window.setTimeout(() => setCopyMsg(""), 2500);
    }
  };

  const canManage = Boolean(access?.canManage);
  const canOperate = Boolean(access?.canOperateLive || access?.canManage);
  const canAccessViewer = Boolean(access?.canAccessViewer);
  const isStaff = Boolean(access && (access.canManage || access.canOperateLive));
  const isAudienceOnly = !canManage && !canOperate && !canAccessViewer;

  const hubLeftImage = useMemo(() => {
    if (!tournament) return "";
    return (
      tStr(tournament, "publicMenuLeftImageUrl", "PublicMenuLeftImageUrl") ||
      tStr(tournament, "imageUrl", "ImageUrl") ||
      ""
    );
  }, [tournament]);

  const hubRightImage = useMemo(() => {
    if (!tournament) return "";
    return (
      tStr(tournament, "publicMenuRightImageUrl", "PublicMenuRightImageUrl") ||
      tStr(tournament, "organizationLogo", "OrganizationLogo") ||
      ""
    );
  }, [tournament]);

  return (
    <div
      className={`mx-auto w-full px-4 py-6 sm:px-6 sm:py-10 ${isAudienceOnly ? "max-w-lg" : "max-w-5xl"}`}
    >
      <RouterLink
        to={user ? "/mis-torneos" : "/"}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-arena"
      >
        <IconArrowLeft size={16} />
        {user ? "Volver a mis torneos" : "Volver al inicio"}
      </RouterLink>

      {!isAudienceOnly ? (
        <div className="relative mb-8 overflow-hidden rounded-3xl border border-arena/10 bg-white/90 p-6 shadow-card backdrop-blur-sm sm:p-8">
          <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-gold via-gold-bright to-arena-bright" />
          <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
            <HubFlank src={hubLeftImage} className="h-14 w-14 shrink-0 object-contain sm:h-[4.5rem] sm:w-[4.5rem]" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <h1 className="font-display text-2xl font-extrabold leading-tight text-ink sm:text-3xl">
                  {loading ? "…" : title}
                </h1>
                {!loading ? (
                  <>
                    <Badge variant={isFinished ? "neutral" : "success"}>
                      {isFinished ? "Finalizado" : "En curso / preparación"}
                    </Badge>
                    <Badge variant={visInfo.variant}>{visInfo.text}</Badge>
                  </>
                ) : null}
              </div>
              {description ? (
                <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted sm:mx-0 sm:text-base">{description}</p>
              ) : null}
              {access ? (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <button
                    type="button"
                    onClick={toggleFavorite}
                    disabled={actionBusy}
                    className="focus-ring inline-flex items-center gap-1.5 rounded-xl border border-arena/20 bg-white/80 px-3 py-1.5 text-sm font-semibold text-ink hover:bg-arena/5"
                  >
                    <IconStar className={access.isFavorite ? "text-gold" : ""} size={16} />
                    {access.isFavorite ? "Siguiendo" : "Seguir torneo"}
                  </button>
                  {access.myRole ? (
                    <span className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                      Tu rol: {roleLabel(access.myRole) || access.myRole}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
            <HubFlank
              src={hubRightImage}
              className="h-14 w-14 shrink-0 rounded-lg object-cover ring-2 ring-arena/10 sm:h-[4.5rem] sm:w-[4.5rem]"
            />
          </div>
        </div>
      ) : null}

      {loading ? <LoadingState className="min-h-[25vh]" /> : null}
      {loadError && !loading ? <Toast kind="error" className="mb-6">{loadError}</Toast> : null}

      {!loading && !loadError ? (
        <div className="flex flex-col gap-8">
          {isAudienceOnly ? (
            <section
              className="relative overflow-hidden rounded-[1.75rem] border border-arena/12 bg-gradient-to-br from-white via-cream-deep/30 to-cream-deep/60 shadow-lift"
              aria-labelledby="public-hub-title"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,162,39,0.12),transparent_50%)]" />
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-gold via-gold-bright to-arena-bright" />
              <div className="relative flex flex-col items-center px-5 pb-10 pt-8 sm:px-8 sm:pb-12 sm:pt-10">
                <div className="flex w-full max-w-sm justify-center gap-4 sm:gap-5">
                  <div className="relative flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-arena/10 bg-white/90 shadow-inner sm:h-32 sm:w-32">
                    <HubMark src={hubLeftImage} />
                  </div>
                  <div className="relative flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-arena/10 bg-white/90 shadow-inner sm:h-32 sm:w-32">
                    <HubMark src={hubRightImage} />
                  </div>
                </div>
                <h1
                  id="public-hub-title"
                  className="mt-8 text-balance text-center font-display text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl"
                >
                  {title}
                </h1>
                {description ? (
                  <p className="mt-3 max-w-md text-pretty text-center text-sm leading-relaxed text-ink-muted sm:text-base">
                    {description}
                  </p>
                ) : null}
                <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
                  <RouterLink
                    to={`/viewgroups/${canonicalId}`}
                    className="focus-ring flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-btn-gold py-3.5 text-center text-base font-extrabold text-ink shadow-md transition hover:brightness-105"
                  >
                    <IconTrophy size={22} className="shrink-0 opacity-90" />
                    Ver grupos
                  </RouterLink>
                  <RouterLink
                    to={`/matches/${canonicalId}`}
                    className="focus-ring flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-btn-arena py-3.5 text-center text-base font-extrabold text-white shadow-md transition hover:brightness-110"
                  >
                    <IconTrophy size={22} className="shrink-0 opacity-95" />
                    Ver partidos
                  </RouterLink>
                  <RouterLink
                    to={`/viewbracket/${canonicalId}`}
                    className="focus-ring flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-gold/50 bg-white/95 py-3.5 text-center text-base font-extrabold text-gold-dim shadow-sm transition hover:border-gold hover:bg-gold/5"
                  >
                    <IconTrophy size={22} className="shrink-0" />
                    Ver bracket
                  </RouterLink>
                  {hasGames ? (
                    <RouterLink
                      to={`/tournamentactions/${canonicalId}/juegos`}
                      className="focus-ring flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-arena/40 bg-white/95 py-3.5 text-center text-base font-extrabold text-arena shadow-sm transition hover:border-arena hover:bg-arena/5"
                    >
                      <IconStar size={22} className="shrink-0" />
                      Ver juegos
                    </RouterLink>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {!isAudienceOnly && canManage ? (
            <section className={cardClass} aria-labelledby="setup-checklist-heading">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 id="setup-checklist-heading" className={sectionTitleClass}>
                    Progreso de configuración
                  </h2>
                  <p className="mt-1 text-sm text-ink-muted">
                    Guía recomendada — puedes saltar pasos y usar <strong>Configurar torneo</strong> cuando quieras.
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-display text-2xl font-bold text-ink">
                    {completedCount}/{wizardSteps.length}
                  </p>
                  <p className="text-xs text-ink-muted">pasos listos</p>
                </div>
              </div>

              <ol className="mt-6 grid gap-3 sm:grid-cols-2">
                {wizardSteps.map((step, idx) => (
                  <li
                    key={step.key}
                    className={`flex gap-3 rounded-xl border px-3 py-3 text-left ${
                      step.done ? "border-emerald-200/80 bg-emerald-50/50" : "border-arena/10 bg-arena/[0.02]"
                    }`}
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                        step.done ? "bg-emerald-600 text-white" : "bg-arena/15 text-ink-muted"
                      }`}
                      aria-hidden
                    >
                      {step.done ? "✓" : idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-ink">{step.label}</p>
                      <p className="text-xs text-ink-muted">{step.hint}</p>
                      {!step.done ? (
                        <RouterLink
                          to={`${step.to}${step.hash}`}
                          className="mt-2 inline-block text-xs font-bold uppercase tracking-wide text-arena hover:underline"
                        >
                          Ir al paso
                        </RouterLink>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>

              {nextStep ? (
                <div className="mt-6 flex flex-col gap-3 border-t border-arena/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-ink-muted">
                    Siguiente recomendado: <strong className="text-ink">{nextStep.label}</strong>
                  </p>
                  <RouterLink to={`${nextStep.to}${nextStep.hash}`} className="sm:shrink-0">
                    <Button variant="gold" className="w-full sm:w-auto">
                      Continuar configuración
                    </Button>
                  </RouterLink>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-3 border-t border-arena/10 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-ink-muted">Configuración base lista. Opera partidos o comparte el visor.</p>
                  {canOperate ? (
                    <RouterLink
                      to={`/tournamentactions/${canonicalId}/mesa`}
                      onClick={() => sessionStorage.setItem(`mesaTournamentName_${canonicalId}`, title)}
                    >
                      <Button variant="primary" className="w-full bg-[#0f6b5c] sm:w-auto">
                        Ir a la mesa
                      </Button>
                    </RouterLink>
                  ) : null}
                </div>
              )}
            </section>
          ) : null}

          {!isAudienceOnly && !canManage && access?.canOperateLive ? (
            <Toast kind="info" className="border border-arena/20 bg-arena/5">
              Tienes permiso de <strong>mesa</strong>: puedes marcar partidos en vivo. La configuración del torneo la gestiona un administrador.
            </Toast>
          ) : null}

          {!isAudienceOnly ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {canManage ? (
              <section className={cardClass}>
                <h2 className={sectionTitleClass}>Configuración</h2>
                <p className="mt-2 text-sm text-ink-muted">Equipos, grupos, calendario, eliminatoria y publicación.</p>
                <nav className="mt-5 flex flex-col gap-2" aria-label="Configuración del torneo">
                  <RouterLink to={`/tournamentactions/${canonicalId}/setup`} className={linkRowClass}>
                    <IconSettings size={18} className="text-arena" />
                    Configurar torneo
                  </RouterLink>
                  <RouterLink to={`/tournamentactions/${canonicalId}/orden-grupos`} className={linkRowClass}>
                    <IconTrophy size={18} className="text-gold-dim" />
                    Desempate manual de grupos
                  </RouterLink>
                  <RouterLink to={`/tournamentactions/${canonicalId}/premium-media`} className={linkRowClass}>
                    <IconStar size={18} className="text-arena" />
                    Estudio premium (carteles + QR)
                  </RouterLink>
                  <RouterLink to={`/tournamentactions/${canonicalId}/audiencia`} className={linkRowClass}>
                    <IconTv size={18} className="text-arena" />
                    Audiencia y seguimiento
                  </RouterLink>
                  <RouterLink to={`/tournamentactions/${canonicalId}/colaboradores`} className={linkRowClass}>
                    <IconUserPlus size={18} className="text-ink-soft" />
                    Colaboradores
                  </RouterLink>
                  <RouterLink to={`/tournamentactions/${canonicalId}/juegos/gestionar`} className={linkRowClass}>
                    <IconStar size={18} className="text-gold-dim" />
                    Juegos (retos y marcas)
                  </RouterLink>
                </nav>
                <div className="mt-6 border-t border-red-200/60 pt-4">
                  <p className={sectionTitleClass + " text-red-600/80"}>Zona de peligro</p>
                  <button
                    type="button"
                    onClick={handleDeleteTournament}
                    disabled={actionBusy}
                    className="focus-ring mt-3 w-full rounded-xl border border-red-300/60 bg-red-50/60 px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:border-red-400 hover:bg-red-100/80 disabled:opacity-50"
                  >
                    Eliminar torneo
                  </button>
                </div>
              </section>
            ) : (
              <section className={`${cardClass} lg:col-span-1`}>
                <h2 className={sectionTitleClass}>Tu acceso</h2>
                <p className="mt-2 text-sm text-ink-muted">
                  {canAccessViewer && !canOperate
                    ? "Cuenta para pantalla o videowall: puedes abrir el modo visor TV. La configuración y la mesa las gestiona otro rol."
                    : isStaff
                      ? "No puedes cambiar la configuración de este torneo."
                      : "Vista de seguimiento. Contacta al organizador si necesitas más permisos."}
                </p>
              </section>
            )}

            <section className={cardClass}>
              <h2 className={sectionTitleClass}>Operación</h2>
              <p className="mt-2 text-sm text-ink-muted">Marcador en vivo y flujo de partidos.</p>
              {canOperate ? (
                <RouterLink
                  to={`/tournamentactions/${canonicalId}/mesa`}
                  onClick={() => sessionStorage.setItem(`mesaTournamentName_${canonicalId}`, title)}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0f6b5c] py-4 text-base font-bold text-white shadow-lg shadow-[#0f6b5c]/25 transition hover:brightness-110"
                >
                  <IconSoccer size={20} />
                  Mesa en vivo
                </RouterLink>
              ) : (
                <p className="mt-4 rounded-xl border border-dashed border-arena/20 bg-arena/[0.03] px-4 py-3 text-sm text-ink-muted">
                  La mesa solo está disponible para operadores o administradores.
                </p>
              )}
              {canOperate && hasGames ? (
                <RouterLink
                  to={`/tournamentactions/${canonicalId}/juegos/gestionar`}
                  className={`${linkRowClass} mt-3`}
                >
                  <IconStar size={18} className="text-gold-dim" />
                  Juegos: registrar marcas
                </RouterLink>
              ) : null}
            </section>

            <section className={cardClass}>
              <h2 className={sectionTitleClass}>Visualización</h2>
              <p className="mt-2 text-sm text-ink-muted">Listas, bracket y modo pantalla completa para el público.</p>
              <nav className="mt-5 flex flex-col gap-2" aria-label="Visualización del torneo">
                <RouterLink to={`/viewgroups/${canonicalId}`} className={linkRowClass}>
                  <IconTrophy size={18} className="text-arena" />
                  Ver grupos
                </RouterLink>
                <RouterLink to={`/matches/${canonicalId}`} className={linkRowClass}>
                  <IconTrophy size={18} className="text-arena" />
                  Ver partidos
                </RouterLink>
                <RouterLink to={`/viewbracket/${canonicalId}`} className={linkRowClass}>
                  <IconTrophy size={18} className="text-arena" />
                  Ver fase final
                </RouterLink>
                {hasGames ? (
                  <RouterLink to={`/tournamentactions/${canonicalId}/juegos`} className={linkRowClass}>
                    <IconStar size={18} className="text-gold-dim" />
                    Ver juegos
                  </RouterLink>
                ) : null}
                {canAccessViewer ? (
                  <RouterLink
                    to={`/viewer/${canonicalId}`}
                    className="flex items-center justify-center gap-2 rounded-2xl border-2 border-arena py-3.5 text-sm font-bold text-arena transition hover:bg-arena/5"
                  >
                    <IconTv size={18} />
                    Modo visor (TV)
                  </RouterLink>
                ) : (
                  <p className="rounded-xl border border-dashed border-arena/20 bg-arena/[0.03] px-4 py-3 text-xs text-ink-muted">
                    El visor a pantalla completa requiere invitación con rol Viewer o permisos de staff.
                  </p>
                )}
              </nav>
              {canAccessViewer ? (
                <div className="mt-4 rounded-xl border border-arena/10 bg-arena/[0.04] p-3">
                  <p className="text-xs font-semibold text-ink-muted">Enlace del visor (inicia sesión en la TV con cuenta Viewer o staff)</p>
                  <p className="mt-1 break-all font-mono text-xs text-ink">{viewerUrl}</p>
                  <Button type="button" variant="outline" size="sm" className="mt-3 w-full" onClick={copyViewerLink}>
                    Copiar enlace
                  </Button>
                  <p className="mt-2 text-xs leading-snug text-ink-muted">
                    Pantalla limpia por defecto. Para carrusel automático o pausa:{" "}
                    <code className="rounded bg-arena/10 px-1">?controls=1</code>
                  </p>
                  {copyMsg ? <p className="mt-2 text-center text-xs text-arena">{copyMsg}</p> : null}
                </div>
              ) : null}
            </section>
          </div>
          ) : null}

          {actionMsg ? <Toast kind="warning" className="max-w-md text-center">{actionMsg}</Toast> : null}
        </div>
      ) : null}
    </div>
  );
}
